import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import ProfileAvatar from "../../../Assets/images/avatar.png";

// Phase F - Car Change. BookingUtils.availableCars does the date-overlap +
// status/verification filtering server-side but deliberately does NOT
// filter by package (confirmed by reading BookingController.availableCars)
// - carChange itself still rejects a mismatched package server-side
// regardless, so this filters the list to same-package cars client-side
// (matching perHourKm against the booking's own preBookDetails.package) to
// avoid offering picks that would just bounce off that check, while still
// relying on the server as the real gate rather than trusting this filter
// alone.
//
// Reuses the existing POST /admin/booking/available-cars endpoint (already
// built and wired, not a bespoke picker) per the plan's own recommendation.

const CarChangeModal = ({ bookingId, currentCarId, startDate, endDate, perHourKm, onClose, onChanged }) => {
    const [loading, setLoading] = useState(true);
    const [cars, setCars] = useState([]);
    const [selectedCarId, setSelectedCarId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const response = await Handler({ method: "post", url: "/admin/booking/available-cars", data: { startDate, endDate, carChangeFor: bookingId } });
            setLoading(false);
            if (response?.success) {
                const list = response?.data?.data?.list || [];
                const filtered = list.filter((c) => c.carId !== currentCarId && (perHourKm == null || (c.priceTypes || []).some((p) => `${p?.perHourKm}` === `${perHourKm}`)));
                setCars(filtered);
            } else {
                toast.error(response?.data?.message || "Could not load available cars");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submitChange = async () => {
        if (!selectedCarId) {
            toast.error("Pick a replacement car");
            return;
        }
        setSubmitting(true);
        const response = await Handler({ method: "patch", url: `/admin/booking/carchange/${bookingId}`, data: { carId: selectedCarId } });
        setSubmitting(false);
        if (response?.success) {
            toast.success("Car changed");
            onChanged?.();
            onClose?.();
        } else {
            toast.error(response?.data?.message || "Could not change the car on this booking");
        }
    };

    return (
        <>
            <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Change Car</h5>
                            <button type="button" className="btn-close" onClick={onClose} disabled={submitting}></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-text mb-2">Showing cars free for this trip's dates with the same package as the current booking.</div>
                            {loading ? (
                                <div className="text-muted small">Loading available cars...</div>
                            ) : cars.length === 0 ? (
                                <div className="text-muted small">No matching cars are free for these dates.</div>
                            ) : (
                                <div className="d-flex flex-column gap-2" style={{ maxHeight: 360, overflowY: "auto" }}>
                                    {cars.map((c) => (
                                        <div
                                            key={c.carId}
                                            className={`d-flex align-items-center gap-3 p-2 rounded-3 border ${selectedCarId === c.carId ? "border-danger bg-danger bg-opacity-10" : ""}`}
                                            style={{ cursor: "pointer" }}
                                            onClick={() => setSelectedCarId(c.carId)}
                                        >
                                            <input type="radio" checked={selectedCarId === c.carId} onChange={() => setSelectedCarId(c.carId)} disabled={submitting} />
                                            <img
                                                src={c.carImages?.[0] || ProfileAvatar}
                                                alt={c.registrationNo}
                                                width={48}
                                                height={48}
                                                className="rounded-3 flex-shrink-0"
                                                style={{ objectFit: "cover" }}
                                            />
                                            <div className="flex-grow-1">
                                                <div className="fw-semibold small">{[c.brand, c.modelName].filter(Boolean).join(" ") || c.registrationNo}</div>
                                                <div className="text-muted small">{c.registrationNo} · {c.hub || "-"}</div>
                                            </div>
                                            <div className="text-muted small text-capitalize">{[c.segment, c.subSegment].filter(Boolean).join(" · ")}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
                                Cancel
                            </button>
                            <button type="button" className="btn btn-primary" onClick={submitChange} disabled={submitting || !selectedCarId}>
                                {submitting ? "Changing..." : "Change Car"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};

export default CarChangeModal;
